import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Client, CreateClient, EditClient } from '../../models/organization/client.model';
import {
  ClientBillingProfile,
  UpsertClientBillingProfile,
} from '../../models/organization/client-billing-profile.model';
import {
  ClientBillingRun,
  ClientBillingRunListItem,
  ClientBillingLine,
  CreateClientBillingRun,
  UpdateClientBillingLine,
  UpsertClientBillingAdditionalLine,
} from '../../models/organization/client-billing-run.model';
import {
  BillingItemType,
  CreateBillingItemType,
  EditBillingItemType,
} from '../../models/organization/billing-item-type.model';
import {
  BillingSssBracket,
  ReplaceBillingSssBrackets,
} from '../../models/organization/billing-sss-bracket.model';
import {
  AgencyProfile,
  UpsertAgencyProfile,
} from '../../models/organization/agency-profile.model';
import {
  BillingSoaSignatory,
  ReplaceBillingSoaSignatories,
} from '../../models/organization/billing-soa-signatory.model';
import { Deployment, CreateDeployment, EditDeployment } from '../../models/organization/deployment.model';
import { Department, CreateDepartment, EditDepartment } from '../../models/organization/department.model';
import { Branch, CreateBranch, EditBranch } from '../../models/organization/branch.model';
import { Position, CreatePosition, EditPosition } from '../../models/organization/position.model';
import { WorkSchedule, CreateWorkSchedule, EditWorkSchedule } from '../../models/organization/work-schedule.model';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  constructor(private http: HttpClient) {}

  // ── Agency profile (tenant company on SOA) ───────────────────────────────────

  getAgencyProfile(): Observable<{ profile: AgencyProfile }> {
    return this.http.get<{ profile: AgencyProfile }>(
      `${environment.organization.root}/agency-profile`,
      { withCredentials: true }
    );
  }

  upsertAgencyProfile(payload: UpsertAgencyProfile): Observable<{ profile: AgencyProfile }> {
    return this.http.put<{ profile: AgencyProfile }>(
      `${environment.organization.root}/agency-profile`,
      payload,
      { withCredentials: true }
    );
  }

  getBillingSoaSignatories(): Observable<{ signatories: BillingSoaSignatory[] }> {
    return this.http.get<{ signatories: BillingSoaSignatory[] }>(
      `${environment.organization.root}/billing-soa-signatories`,
      { withCredentials: true }
    );
  }

  replaceBillingSoaSignatories(
    payload: ReplaceBillingSoaSignatories
  ): Observable<{ signatories: BillingSoaSignatory[] }> {
    return this.http.put<{ signatories: BillingSoaSignatory[] }>(
      `${environment.organization.root}/billing-soa-signatories`,
      payload,
      { withCredentials: true }
    );
  }

  uploadBillingSoaSignature(
    signatoryGuid: string,
    file: File
  ): Observable<{ signatory: BillingSoaSignatory }> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<{ signatory: BillingSoaSignatory }>(
      `${environment.organization.root}/billing-soa-signatories/${signatoryGuid}/signature`,
      form,
      { withCredentials: true }
    );
  }

  clearBillingSoaSignature(signatoryGuid: string): Observable<{ signatory: BillingSoaSignatory }> {
    return this.http.delete<{ signatory: BillingSoaSignatory }>(
      `${environment.organization.root}/billing-soa-signatories/${signatoryGuid}/signature`,
      { withCredentials: true }
    );
  }

  // ── Clients (staffing) ───────────────────────────────────────────────────────

  getClients(): Observable<{ clients: Client[] }> {
    return this.http.get<{ clients: Client[] }>(
      `${environment.organization.root}/clients`,
      { withCredentials: true }
    );
  }

  getClientByGuid(clientGuid: string): Observable<{ client: Client | null }> {
    return this.http.get<{ client: Client | null }>(
      `${environment.organization.root}/clients/${clientGuid}`,
      { withCredentials: true }
    );
  }

  createClient(payload: CreateClient): Observable<{ client: Client }> {
    return this.http.post<{ client: Client }>(
      `${environment.organization.root}/clients`,
      payload,
      { withCredentials: true }
    );
  }

  editClient(clientGuid: string, payload: EditClient): Observable<{ client: Client }> {
    return this.http.put<{ client: Client }>(
      `${environment.organization.root}/clients/${clientGuid}`,
      payload,
      { withCredentials: true }
    );
  }

  deleteClient(clientGuid: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.organization.root}/clients/${clientGuid}`,
      { withCredentials: true }
    );
  }

  getClientBillingProfile(clientGuid: string): Observable<{ profile: ClientBillingProfile | null }> {
    return this.http.get<{ profile: ClientBillingProfile | null }>(
      `${environment.organization.root}/clients/${clientGuid}/billing-profile`,
      { withCredentials: true }
    );
  }

  upsertClientBillingProfile(
    clientGuid: string,
    payload: UpsertClientBillingProfile
  ): Observable<{ profile: ClientBillingProfile }> {
    return this.http.put<{ profile: ClientBillingProfile }>(
      `${environment.organization.root}/clients/${clientGuid}/billing-profile`,
      payload,
      { withCredentials: true }
    );
  }

  // ── Client billing runs ──────────────────────────────────────────────────────

  getClientBillingRuns(): Observable<{ runs: ClientBillingRunListItem[] }> {
    return this.http.get<{ runs: ClientBillingRunListItem[] }>(
      `${environment.organization.root}/client-billing-runs`,
      { withCredentials: true }
    );
  }

  getClientBillingRunByGuid(runGuid: string): Observable<{ run: ClientBillingRun | null }> {
    return this.http.get<{ run: ClientBillingRun | null }>(
      `${environment.organization.root}/client-billing-runs/${runGuid}`,
      { withCredentials: true }
    );
  }

  createClientBillingRun(payload: CreateClientBillingRun): Observable<{ run: ClientBillingRun }> {
    return this.http.post<{ run: ClientBillingRun }>(
      `${environment.organization.root}/client-billing-runs`,
      payload,
      { withCredentials: true }
    );
  }

  processClientBillingRun(runGuid: string): Observable<{ run: ClientBillingRun }> {
    return this.http.post<{ run: ClientBillingRun }>(
      `${environment.organization.root}/client-billing-runs/${runGuid}/process`,
      {},
      { withCredentials: true }
    );
  }

  updateClientBillingSssEcOverride(
    runGuid: string,
    payload: {
      sssEcModeOverride: number | null;
      includePhicErOverride: boolean | null;
      includeHdmfErOverride: boolean | null;
    }
  ): Observable<{ run: ClientBillingRun }> {
    return this.http.put<{ run: ClientBillingRun }>(
      `${environment.organization.root}/client-billing-runs/${runGuid}/sss-ec-override`,
      payload,
      { withCredentials: true }
    );
  }

  importClientBillingHoursFromDtr(runGuid: string): Observable<{ run: ClientBillingRun }> {
    return this.http.post<{ run: ClientBillingRun }>(
      `${environment.organization.root}/client-billing-runs/${runGuid}/import-hours`,
      {},
      { withCredentials: true }
    );
  }

  importClientBillingFromPayroll(runGuid: string): Observable<{ run: ClientBillingRun }> {
    return this.http.post<{ run: ClientBillingRun }>(
      `${environment.organization.root}/client-billing-runs/${runGuid}/import-payroll`,
      {},
      { withCredentials: true }
    );
  }

  updateClientBillingLine(
    runGuid: string,
    lineGuid: string,
    payload: UpdateClientBillingLine
  ): Observable<{ line: ClientBillingLine }> {
    return this.http.put<{ line: ClientBillingLine }>(
      `${environment.organization.root}/client-billing-runs/${runGuid}/lines/${lineGuid}`,
      payload,
      { withCredentials: true }
    );
  }

  deleteClientBillingRun(runGuid: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.organization.root}/client-billing-runs/${runGuid}`,
      { withCredentials: true }
    );
  }

  downloadClientBillingSoaPdf(runGuid: string): Observable<Blob> {
    return this.http.get(
      `${environment.organization.root}/client-billing-runs/${runGuid}/soa.pdf`,
      { responseType: 'blob', withCredentials: true }
    );
  }

  addClientBillingAdditionalLine(
    runGuid: string,
    payload: UpsertClientBillingAdditionalLine
  ): Observable<{ run: ClientBillingRun }> {
    return this.http.post<{ run: ClientBillingRun }>(
      `${environment.organization.root}/client-billing-runs/${runGuid}/additional-lines`,
      payload,
      { withCredentials: true }
    );
  }

  updateClientBillingAdditionalLine(
    runGuid: string,
    lineGuid: string,
    payload: UpsertClientBillingAdditionalLine
  ): Observable<{ run: ClientBillingRun }> {
    return this.http.put<{ run: ClientBillingRun }>(
      `${environment.organization.root}/client-billing-runs/${runGuid}/additional-lines/${lineGuid}`,
      payload,
      { withCredentials: true }
    );
  }

  deleteClientBillingAdditionalLine(
    runGuid: string,
    lineGuid: string
  ): Observable<{ run: ClientBillingRun }> {
    return this.http.delete<{ run: ClientBillingRun }>(
      `${environment.organization.root}/client-billing-runs/${runGuid}/additional-lines/${lineGuid}`,
      { withCredentials: true }
    );
  }

  // ── Billing item catalog ─────────────────────────────────────────────────────

  getBillingItemTypes(): Observable<{ items: BillingItemType[] }> {
    return this.http.get<{ items: BillingItemType[] }>(
      `${environment.organization.root}/billing-item-types`,
      { withCredentials: true }
    );
  }

  createBillingItemType(payload: CreateBillingItemType): Observable<{ item: BillingItemType }> {
    return this.http.post<{ item: BillingItemType }>(
      `${environment.organization.root}/billing-item-types`,
      payload,
      { withCredentials: true }
    );
  }

  editBillingItemType(
    itemGuid: string,
    payload: EditBillingItemType
  ): Observable<{ item: BillingItemType }> {
    return this.http.put<{ item: BillingItemType }>(
      `${environment.organization.root}/billing-item-types/${itemGuid}`,
      payload,
      { withCredentials: true }
    );
  }

  deleteBillingItemType(itemGuid: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.organization.root}/billing-item-types/${itemGuid}`,
      { withCredentials: true }
    );
  }

  // ── Billing SSS brackets ─────────────────────────────────────────────────────

  getBillingSssBrackets(): Observable<{ items: BillingSssBracket[] }> {
    return this.http.get<{ items: BillingSssBracket[] }>(
      `${environment.organization.root}/billing-sss-brackets`,
      { withCredentials: true }
    );
  }

  replaceBillingSssBrackets(
    payload: ReplaceBillingSssBrackets
  ): Observable<{ items: BillingSssBracket[] }> {
    return this.http.put<{ items: BillingSssBracket[] }>(
      `${environment.organization.root}/billing-sss-brackets`,
      payload,
      { withCredentials: true }
    );
  }

  resetBillingSssBrackets(): Observable<{ items: BillingSssBracket[] }> {
    return this.http.post<{ items: BillingSssBracket[] }>(
      `${environment.organization.root}/billing-sss-brackets/reset`,
      {},
      { withCredentials: true }
    );
  }

  // ── Deployments (staffing) ───────────────────────────────────────────────────

  getDeployments(): Observable<{ deployments: Deployment[] }> {
    return this.http.get<{ deployments: Deployment[] }>(
      `${environment.organization.root}/deployments`,
      { withCredentials: true }
    );
  }

  getDeploymentByGuid(deploymentGuid: string): Observable<{ deployment: Deployment | null }> {
    return this.http.get<{ deployment: Deployment | null }>(
      `${environment.organization.root}/deployments/${deploymentGuid}`,
      { withCredentials: true }
    );
  }

  createDeployment(payload: CreateDeployment): Observable<{ deployment: Deployment }> {
    return this.http.post<{ deployment: Deployment }>(
      `${environment.organization.root}/deployments`,
      payload,
      { withCredentials: true }
    );
  }

  editDeployment(deploymentGuid: string, payload: EditDeployment): Observable<{ deployment: Deployment }> {
    return this.http.put<{ deployment: Deployment }>(
      `${environment.organization.root}/deployments/${deploymentGuid}`,
      payload,
      { withCredentials: true }
    );
  }

  deleteDeployment(deploymentGuid: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.organization.root}/deployments/${deploymentGuid}`,
      { withCredentials: true }
    );
  }

  // ── Departments ──────────────────────────────────────────────────────────────

  getDepartments(): Observable<{ departments: Department[] }> {
    return this.http.get<{ departments: Department[] }>(
      `${environment.organization.root}/departments`,
      { withCredentials: true }
    );
  }

  getDepartmentByGuid(departmentGuid: string): Observable<{ department: Department | null }> {
    return this.http.get<{ department: Department | null }>(
      `${environment.organization.root}/departments/${departmentGuid}`,
      { withCredentials: true }
    );
  }

  createDepartment(payload: CreateDepartment): Observable<{ department: Department }> {
    return this.http.post<{ department: Department }>(
      `${environment.organization.root}/departments`,
      payload,
      { withCredentials: true }
    );
  }

  editDepartment(departmentGuid: string, payload: EditDepartment): Observable<{ department: Department }> {
    return this.http.put<{ department: Department }>(
      `${environment.organization.root}/departments/${departmentGuid}`,
      payload,
      { withCredentials: true }
    );
  }

  deleteDepartment(departmentGuid: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.organization.root}/departments/${departmentGuid}`,
      { withCredentials: true }
    );
  }

  // ── Branches ─────────────────────────────────────────────────────────────────

  getBranches(): Observable<{ branches: Branch[] }> {
    return this.http.get<{ branches: Branch[] }>(
      `${environment.organization.root}/branches`,
      { withCredentials: true }
    );
  }

  getBranchByGuid(branchGuid: string): Observable<{ branch: Branch | null }> {
    return this.http.get<{ branch: Branch | null }>(
      `${environment.organization.root}/branches/${branchGuid}`,
      { withCredentials: true }
    );
  }

  createBranch(payload: CreateBranch): Observable<{ branch: Branch }> {
    return this.http.post<{ branch: Branch }>(
      `${environment.organization.root}/branches`,
      payload,
      { withCredentials: true }
    );
  }

  editBranch(branchGuid: string, payload: EditBranch): Observable<{ branch: Branch }> {
    return this.http.put<{ branch: Branch }>(
      `${environment.organization.root}/branches/${branchGuid}`,
      payload,
      { withCredentials: true }
    );
  }

  deleteBranch(branchGuid: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.organization.root}/branches/${branchGuid}`,
      { withCredentials: true }
    );
  }

  // ── Positions ────────────────────────────────────────────────────────────────

  getPositions(): Observable<{ positions: Position[] }> {
    return this.http.get<{ positions: Position[] }>(
      `${environment.organization.root}/positions`,
      { withCredentials: true }
    );
  }

  getPositionByGuid(positionGuid: string): Observable<{ position: Position | null }> {
    return this.http.get<{ position: Position | null }>(
      `${environment.organization.root}/positions/${positionGuid}`,
      { withCredentials: true }
    );
  }

  getPositionsByDepartment(departmentGuid: string): Observable<{ positions: Position[] }> {
    return this.http.get<{ positions: Position[] }>(
      `${environment.organization.root}/departments/${departmentGuid}/positions`,
      { withCredentials: true }
    );
  }

  createPosition(payload: CreatePosition): Observable<{ position: Position }> {
    return this.http.post<{ position: Position }>(
      `${environment.organization.root}/positions`,
      payload,
      { withCredentials: true }
    );
  }

  editPosition(positionGuid: string, payload: EditPosition): Observable<{ position: Position }> {
    return this.http.put<{ position: Position }>(
      `${environment.organization.root}/positions/${positionGuid}`,
      payload,
      { withCredentials: true }
    );
  }

  deletePosition(positionGuid: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.organization.root}/positions/${positionGuid}`,
      { withCredentials: true }
    );
  }

  // ── Work Schedules ───────────────────────────────────────────────────────────

  getWorkSchedules(): Observable<{ workSchedules: WorkSchedule[] }> {
    return this.http.get<{ workSchedules: WorkSchedule[] }>(
      `${environment.organization.root}/work-schedules`,
      { withCredentials: true }
    );
  }

  getWorkScheduleByGuid(workScheduleGuid: string): Observable<{ workSchedule: WorkSchedule | null }> {
    return this.http.get<{ workSchedule: WorkSchedule | null }>(
      `${environment.organization.root}/work-schedules/${workScheduleGuid}`,
      { withCredentials: true }
    );
  }

  createWorkSchedule(payload: CreateWorkSchedule): Observable<{ workSchedule: WorkSchedule }> {
    return this.http.post<{ workSchedule: WorkSchedule }>(
      `${environment.organization.root}/work-schedules`,
      payload,
      { withCredentials: true }
    );
  }

  editWorkSchedule(workScheduleGuid: string, payload: EditWorkSchedule): Observable<{ workSchedule: WorkSchedule }> {
    return this.http.put<{ workSchedule: WorkSchedule }>(
      `${environment.organization.root}/work-schedules/${workScheduleGuid}`,
      payload,
      { withCredentials: true }
    );
  }

  deleteWorkSchedule(workScheduleGuid: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.organization.root}/work-schedules/${workScheduleGuid}`,
      { withCredentials: true }
    );
  }
}
